import { CheckCircle, Edit, Plus } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsAssetGroupManagement,
  type GoogleAdsAssetGroupManagementResult,
} from 'sprites-types/workflow/tools/googleAdsAssetGroupManagement'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleAdsAssetGroupManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsAssetGroupManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsAssetGroupManagementResult
  const isCreate = 'assetGroupId' in result && result.assetGroupId !== null
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined
  const assetGroupId = 'assetGroupId' in result ? result.assetGroupId : undefined

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-purple-100 p-2">
            {isCreate ? (
              <Plus className="h-5 w-5 text-purple-600" />
            ) : (
              <Edit className="h-5 w-5 text-purple-600" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">
              {isCreate ? 'Asset Group Created' : 'Asset Group Updated'}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
            {assetGroupId && (
              <div className="mb-1 text-xs text-gray-500">Asset Group ID: {assetGroupId}</div>
            )}
            {resourceName && (
              <div className="mb-1 text-xs text-gray-500">
                Resource: {resourceName.split('/').pop()}
              </div>
            )}
            {message && <div className="text-xs text-gray-700">{message}</div>}
          </div>

          {isCreate && (
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Asset group ready for Performance Max campaign</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsAssetGroupManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsAssetGroupManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsAssetGroupManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsAssetGroupManagementResult
  const isCreate = 'assetGroupId' in result && result.assetGroupId !== null
  const assetGroupId = 'assetGroupId' in result ? result.assetGroupId : undefined
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined

  return `## Google Ads Asset Group ${isCreate ? 'Created' : 'Updated'}

${assetGroupId ? `**Asset Group ID:** ${assetGroupId}` : ''}
${resourceName ? `**Resource:** ${resourceName}` : ''}
${message ? `**Message:** ${message}` : ''}

---
`
}

export const GoogleAdsAssetGroupManagementDisplay = createWorkflowToolComponent(
  GoogleAdsAssetGroupManagementDisplayComponent,
  downloadGoogleAdsAssetGroupManagement,
  googleAdsAssetGroupManagementToMarkdown
)
